import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import {
  Filter,
  ArrowUp,
  ArrowDown,
  Loader2,
  Search,
  ChevronDown,
  ChevronUp,
  BarChart3,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../Components/UI/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/UI/table";
import { getUsersArr } from "../../Store/Reducers";
import {
  formatAmount,
  getPastMonthOptions,
  months,
  FormatAmount,
  getOtherYearOptions,
  getPastQuarterOptions,
  safeParseJsonArray
} from "../../Utils/Utilities";
import { CommonFilter } from "../../Components/Common/CommonFilter";
import PeriodFilter from "../../Components/Common/PeriodFilter";
import { cn } from "../../Lib/Cn";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
} from "../../Components/UI/drawer";
import DrawerFilter from "../../Components/Common/DrawerFilter";
import { RiResetLeftFill } from "react-icons/ri";
import useIsMobile from "../../Hooks/useIsMobile";
import { getAgencyData } from "../../Utils/Storage";
import { useCurrency } from "../../Hooks/useCurrency";
import { setGlobalLoading } from "../../Store/Actions"; // Import your global loading action
import { checkAPIResponse } from "../../Utils/APIResponseUtils";
import { getEffortsLogAPI } from "./UsersAPI";
import ColumnPreferencesDropdown from "../../Components/Common/ColumnPrefereceDD";
import { Badge } from "../../Components/UI/badge";
import Pagination from "../../Components/Common/Pagination";


import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
  Legend,
  Cell
} from "recharts";
import ConditionalDiv from "../../Components/Common/ConditionalDiv";
// --- CHART CONFIGURATION ---
const colorPalette = [
  "#0c6980",
  "#0b5d1e",
  "#d4af37",
  "#bf5700",
  "#230e2eff",
  "#c44536",
  "#2a9d8f",
  "#8a5a44",
  "#3d348b",
  "#264653",
];

const getCurrentYearMonth = () => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1 // JavaScript months are 0-indexed, so add 1
  };
};

// Helper function to parse month string (e.g., "January 2024" -> {year: 2024, month: 1})
const parseMonthString = (monthStr) => {
  if (!monthStr || monthStr === "all") {
    return getCurrentYearMonth();
  }

  const parts = monthStr.split(" ");
  if (parts.length !== 2) {
    return getCurrentYearMonth();
  }

  const monthName = parts[0];
  const year = parseInt(parts[1]);
  const monthIndex = months.findIndex(m => m === monthName);

  return {
    year: year,
    month: monthIndex + 1 // months array is 0-indexed
  };
};

// Helper function to parse quarter string (e.g., "Q1 2024" -> {quarter: 1, year: 2024})
const parseQuarterString = (quarterStr) => {
  if (!quarterStr || quarterStr === "all") {
    return null;
  }

  const match = quarterStr.match(/Q(\d)\s+(\d{4})/);
  if (!match) {
    return null;
  }

  return {
    quarter: parseInt(match[1]),
    year: parseInt(match[2])
  };
};

const getPrdTypeFromFilters = (filters) => {
  const { dateRange, otherQuarter } = filters;
  const now = new Date();
  const currentMonth = now.getMonth() + 1;

  switch (dateRange) {
    case "This Month":
    case "Other Month":
      return "MM";
    case "This Quarter":
      return `QQ${Math.ceil(currentMonth / 3)}`;
    case "Other Quarter":
      if (otherQuarter && otherQuarter !== "all") {
        const match = otherQuarter.match(/Q(\d)/);
        return match ? `QQ${match[1]}` : "MM";
      }
      return `QQ${Math.ceil(currentMonth / 3)}`;
    case "This Year":
    case "Other Year":
      return "YY";
    default:
      return "MM";
  }
};

// Ensure this helper handles the strings correctly
const getYearMonthFromFilters = (filters) => {
  const { dateRange, otherMonth } = filters;
  const now = new Date();

  if (dateRange === "Other Month") {
    // Splits "January 2026" or "Jan 2026"
    const parts = otherMonth.split(" ");
    if (parts.length === 2) {
      const mIndex = months.findIndex(m => m.startsWith(parts[0]));
      return {
        year: parseInt(parts[1]) || now.getFullYear(),
        month: mIndex !== -1 ? mIndex + 1 : now.getMonth() + 1
      };
    }
  }
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};



const EffortsLog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const clickedTabRef = useRef(null);
  const currencySymbol = useCurrency();
  const dispatch = useDispatch();

  // Query Params
  const searchParams = new URLSearchParams(location.search);
  const uidParam = searchParams.get("uid");
  const monthParam = searchParams.get("m");

  const users = useSelector(getUsersArr);
  const agencyData = getAgencyData();
  const countryID = agencyData?.CountryID || "";

  const [isLoading, setIsLoading] = useState(false);
  const [effortsData, setEffortsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });
  // const [isFilterSelected, setIsFilterSelected] = useState(!isMobile );
  const [isFilterSelected, setIsFilterSelected] = useState(!isMobile);
  const prevFiltersRef = useRef({ prdType: null, year: null, month: null });
//   const [monthlyThreads, setMonthlyThreads] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("assignedTo");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: connectionData } = useSelector((state) => state.connectionData);
  const { data: activeData} = useSelector((state)=> state.activeData);
  const monthlyThreads = JSON.parse(activeData.MonthlyThreads);

  const recordTypes = useSelector(
    (state) => state.masterData.recordTypes || []
  );

  const oldestSinceDT = useMemo(() => {
    if (!connectionData || connectionData.length === 0) return new Date();

    const dates = connectionData
      .map((conn) => {
        // Use SinceDT if available, otherwise fallback to CreatedDT
        const dateStr = conn.SinceDT || conn.CreatedDT;
        return dateStr ? new Date(dateStr) : null;
      })
      .filter((d) => d !== null && !isNaN(d.getTime()));

    if (dates.length === 0) return new Date();

    // Return the oldest date found
    return new Date(Math.min(...dates));
  }, [connectionData]);

  // --- CHART STATE ---
  const [activeChartFilters, setActiveChartFilters] = useState([]);
  // Set dynamic tab title
  useEffect(() => {
    // 1. Capture the title that exists BEFORE the user enters this page
    const previousTitle = document.title;

    // 2. Set the static title for this report
    document.title = "Team Efforts Summary";

    // 3. THE CLEANUP: This runs when the user navigates away
    return () => {
      document.title = "SahiClub";
    };
  }, []); // Empty dependency array means this only runs on Mount and Unmount;



  // REMOVED: The duplicate useEffect that triggered on monthlyThreads alone has been removed.

  const getMonthFilterFromNumber = (monthNumber) => {
    if (!monthNumber) return { dateRange: "This Month", otherMonth: "all" };

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const targetMonth = parseInt(monthNumber, 10);

    if (targetMonth === currentMonth) {
      return { dateRange: "This Month", otherMonth: "all" };
    } else {
      const monthName = months[targetMonth - 1];
      return {
        dateRange: "Other Month",
        otherMonth: `${monthName} ${currentYear}`
      };
    }
  };

  const initialMonthFilters = getMonthFilterFromNumber(monthParam);

  const getInitialFilters = () => {
    const now = new Date();
    const searchParams = new URLSearchParams(location.search);
    const uidParam = searchParams.get("uid");

    // Current strings to match dropdown options exactly
    const currentMonthStr = `${months[now.getMonth()]} ${now.getFullYear()}`;
    const currentQuarterStr = `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`;
    const currentYearStr = now.getFullYear().toString();

    return {
      assignedTo: uidParam || "all",
      dateRange: "This Month",
      otherMonth: currentMonthStr,
      otherQuarter: currentQuarterStr,
      otherYear: currentYearStr
    };
  };

  const [selectedFilters, setSelectedFilters] = useState(getInitialFilters());
  const [tempFilters, setTempFilters] = useState(selectedFilters);




  const filterOptions = [
    {
      key: "dateRange",
      label: "Period", // This will be handled by the display logic
      options: ["This Month", "Other Month", "This Quarter", "Other Quarter", "This Year", "Other Year"],
      type: "options",
    },
    {
      key: "otherMonth",
      label: "Select Month",
      options: getPastMonthOptions(oldestSinceDT),
      displayValue: (val) => {
        if (!val || val === "all") {
          const now = new Date();
          return `${months[now.getMonth()]} ${now.getFullYear()}`;
        }
        const parts = val.split(" ");
        return parts.length === 2 ? `${parts[0].substring(0, 3)} ${parts[1]}` : val;
      },
      type: "list",
      searchEnabled: true,
    },
    {
      key: "otherQuarter",
      label: "Select Quarter",
      options: getPastQuarterOptions(oldestSinceDT), // Now uses dynamic date
      disabled: selectedFilters.dateRange !== "Other Quarter" && tempFilters.dateRange !== "Other Quarter",
      mobiledisabled: tempFilters.dateRange !== "Other Quarter",
      type: "list",
      mobileDropdownText: "Select Quarter",
      mobileNoLabel: true,
      isSorting: false,
    },
    {
      key: "otherYear",
      label: "Select Year",
      options: useMemo(() => {
        return getOtherYearOptions(oldestSinceDT);
      }, [oldestSinceDT]),
      displayValue: (val) => {
        if (!val || val === "all") {
          return new Date().getFullYear().toString();
        }
        return val;
      },
      type: "list",
    }
  ];
  useEffect(() => {
    if (monthlyThreads.length > 0) {
      fetchEffortsData();
    }
  }, [
    selectedFilters.dateRange,
    selectedFilters.otherMonth,
    selectedFilters.otherQuarter,
    selectedFilters.otherYear
  ]);

  // Handle URL params
  useEffect(() => {
    if (uidParam || monthParam) {
      setIsFilterSelected(true);
    }
  }, [uidParam, monthParam]);

  // Mobile Filter Scrollbar Event Listener
  useEffect(() => {
    const waitAndAttachScroll = () => {
      const container = document.querySelector(".filter-scroll-container");

      if (!container) {
        setTimeout(waitAndAttachScroll, 50);
        return;
      }

      const handleScroll = () => {
        if (clickedTabRef.current) return;

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        const scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;
        const sidebarIcons = [
          { key: "assignedTo" },
          { key: "dateRange" }
        ];
        const index = Math.min(
          Math.floor((scrollPercent / 100) * sidebarIcons.length),
          sidebarIcons.length - 1
        );
        const key = sidebarIcons[index]?.key;
        setActiveTab((prev) => (key !== prev ? key : prev));
      };
      container.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => container.removeEventListener("scroll", handleScroll);
    };

    waitAndAttachScroll();
  }, [isFilterSelected]);

  useEffect(() => {
    if (isFilterSelected && isMobile) {
      setIsDrawerOpen(true);
      setTempFilters(selectedFilters);
    }
  }, [isFilterSelected, isMobile]);

  // Replace the fetchEffortsData function with this updated version:
  const loadedParamsRef = useRef(null);
  const fetchEffortsData = async () => {
    try {
      const now = new Date();
      let year = now.getFullYear();
      let month = now.getMonth() + 1;
      let prdType = "MM";

      // 1. Resolve Period Parameters
      const range = selectedFilters.dateRange;

      // --- FIX START: Handle Cleared States ---
      if (range === "Other Month" && (selectedFilters.otherMonth === "all" || !selectedFilters.otherMonth)) return;
      if (range === "Other Quarter" && (selectedFilters.otherQuarter === "all" || !selectedFilters.otherQuarter)) return;
      if (range === "Other Year" && (selectedFilters.otherYear === "all" || !selectedFilters.otherYear)) return;

      if (range === "This Month") {
        prdType = "MM";
      } else if (range === "Other Month") {
        const { year: y, month: m } = getYearMonthFromFilters(selectedFilters);
        year = y;
        month = m;
        prdType = "MM";
      } else if (range === "This Quarter") {
        prdType = `QQ${Math.ceil(month / 3)}`;
      } else if (range === "Other Quarter") {
        const qMatch = selectedFilters.otherQuarter.match(/Q(\d)\s+(\d{4})/);
        if (qMatch) {
          prdType = `QQ${qMatch[1]}`;
          year = parseInt(qMatch[2]);
        }
      } else if (range === "This Year") {
        prdType = "YY";
      } else if (range === "Other Year") {
        prdType = "YY";
        year = parseInt(selectedFilters.otherYear) || now.getFullYear();
      }

      // 2. GUARD: Prevents redundant API calls if period hasn't changed
      const prev = prevFiltersRef.current;
      if (
        prev.prdType === prdType &&
        prev.year === year &&
        prev.month === month &&
        effortsData.length > 0
      ) {
        return;
      }

      // 3. API Execution
      dispatch(setGlobalLoading(true));
      setIsLoading(true);

      const paramsAPI = {
        prdType: prdType,
        prdQty: "1",
        year: year.toString(),
        month: month.toString()
      };

      const data = await getEffortsLogAPI(paramsAPI);
      checkAPIResponse(data);

      const updatePkg = JSON.parse(data.updatePackage);
      const apiData = updatePkg.Table1 || [];

      // Logic for Processing and Merging
      const interactionTypes = recordTypes.map(typ => typ.Typ);
      const groupedByUID = {};

      apiData.forEach(record => {
        const { RecUserID, RecType, Cnt } = record;
        if (!groupedByUID[RecUserID]) {
          groupedByUID[RecUserID] = { UID: RecUserID };
          interactionTypes.forEach(t => groupedByUID[RecUserID][t] = 0);
        }
        if (RecType) groupedByUID[RecUserID][RecType] = Cnt;
      });

      const transformedApiData = Object.values(groupedByUID);

      let monthsToInclude = [];
      if (prdType.startsWith("QQ")) {
        const qNum = parseInt(prdType.replace("QQ", ""));
        const startMonth = (qNum - 1) * 3 + 1;
        monthsToInclude = [startMonth, startMonth + 1, startMonth + 2];
      } else if (prdType === "YY") {
        monthsToInclude = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      } else {
        monthsToInclude = [month];
      }

      const orderStats = monthlyThreads?.filter(thr =>
        thr.TID == 2 && thr.YY == year && monthsToInclude.includes(thr.MM)
      ) || [];

      // Final Merge
      const mergedData = users.map(user => {
        const apiRecord = transformedApiData.find(r => r.UID === user.userID);
        const usrOrderStats = { val: 0, count: 0 };

        orderStats.forEach(os => {
          const uStat = safeParseJsonArray(os.Usrs).find(u => u.UID === user.userID);
          if (uStat) {
            usrOrderStats.val += uStat.RA || 0;
            usrOrderStats.count += uStat.RC || 0;
          }
        });

        return {
          UID: user.userID,
          Executive: user.name,
          Value: usrOrderStats.val,
          Orders: usrOrderStats.count,
          ...(apiRecord || interactionTypes.reduce((a, c) => ({ ...a, [c]: 0 }), {}))
        };
      });

      const sorted = mergedData.sort((a, b) => b.Value - a.Value);
      setEffortsData(sorted);
      setFilteredData(sorted);

      // Update Ref to current parameters
      prevFiltersRef.current = { prdType, year, month };

    } catch (error) {
      console.error("Fetch Error:", error);
      setEffortsData([]);
    } finally {
      dispatch(setGlobalLoading(false));
      setIsLoading(false);
    }
  };
  // Get dynamic columns from data
  const dynamicColumns = useMemo(() => {
    if (effortsData.length === 0) {
      // Return default columns when no data is available
      return ["Value"]; 
    }
    const firstRow = effortsData[0];
    // Exclude UID, Executive, and userName from columns
    return Object.keys(firstRow).filter(key =>
      key !== "UID" && key !== "Executive" && key !== "userName" && key !== "Sale" && key !== "Sales"
    );
  }, [effortsData]);

  // Column Preferences State
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const storedPrefs = localStorage.getItem("EffortLogColPrefs");
    if (storedPrefs) {
      try {
        return JSON.parse(storedPrefs);
      } catch (e) { console.error(e); }
    }
    return {};
  });
  useEffect(() => {
    // Call the function regardless of monthlyThreads length
    fetchEffortsData();
  }, [
    selectedFilters// Still include this if merging logic depends on it
  ]);

  // Initialize visibility when dynamic columns change
  useEffect(() => {
    if (dynamicColumns.length > 0) {
      setColumnVisibility(prev => {
        const newVis = { ...prev };
        let hasChanges = false;
        dynamicColumns.forEach(col => {
          if (newVis[col] === undefined) {
            // Default to true only if it is Value or isinxn is true in recordTypes
            if (col === "Value") {
              newVis[col] = true;
            } else {
              const recType = recordTypes.find(rt => rt.Typ === col);
              newVis[col] = recType ? recType.IsInxn : false;
            }
            hasChanges = true;
          }
        });
        return hasChanges ? newVis : prev;
      });
    }
  }, [dynamicColumns, recordTypes]);

  // Save to local storage
  useEffect(() => {
    if (Object.keys(columnVisibility).length > 0) {
      localStorage.setItem("EffortLogColPrefs", JSON.stringify(columnVisibility));
    }
  }, [columnVisibility]);

  // Column definitions for the dropdown
  const columnDefinitions = useMemo(() => {
    return dynamicColumns
      .filter(col => col !== "Value" && col !== "Orders")
      .map(col => ({
        key: col,
        label: col
      }));
  }, [dynamicColumns]);

  // Filter by executive and search term
  useEffect(() => {
    let filtered = effortsData;

    // Filter by executive
    if (selectedFilters.assignedTo !== "all") {
      filtered = filtered.filter(row => row.UID === selectedFilters.assignedTo);
    }

    // Filter by search term
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((row) => {
        const user = users.find(u => u.userID === row.UID);
        const userName = user?.name?.toLowerCase() || "";

        // Search in user name and all dynamic columns
        const fieldsToSearch = [
          userName,
          ...dynamicColumns.map(col => row[col]?.toString().toLowerCase())
        ];

        return fieldsToSearch
          .filter(Boolean)
          .some((field) => field.includes(term));
      });
    }

    setFilteredData(filtered);
  }, [
    searchTerm,
    effortsData,
    selectedFilters.assignedTo,
    users,
    dynamicColumns,
  ]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilters]);
  // Sort functionality
  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      aValue = String(aValue || "").toLowerCase();
      bValue = String(bValue || "").toLowerCase();
      if (sortConfig.direction === "asc") return aValue > bValue ? 1 : -1;
      else return aValue < bValue ? 1 : -1;
    });
  }, [filteredData, sortConfig, users]);

  // --- CHART DATA PROCESSING ---
  // Updated validChartColumns to hide types with zero total data
  const validChartColumns = useMemo(() => {
    const typeOrder = recordTypes.map((rt) => rt.Typ);

    return dynamicColumns
      .filter((col) => {
        // 1. Exclude non-interaction columns
        if (col === "Value" || col === "Orders") return false;

        // 2. Exclude if user manually hid it in Column Preferences
        if (columnVisibility[col] === false) return false;

        // 3. NEW: Exclude if the total sum of this column across all rows is 0
        const totalSumForCol = filteredData.reduce((sum, row) => sum + (Number(row[col]) || 0), 0);
        return totalSumForCol > 0;
      })
      .sort((a, b) => typeOrder.indexOf(a) - typeOrder.indexOf(b));
  }, [dynamicColumns, columnVisibility, recordTypes, filteredData]);


  const dynamicColorMap = useMemo(() => {
    const map = {};
    validChartColumns.forEach((type, index) => {
      map[type] = colorPalette[index % colorPalette.length];
    });
    return map;
  }, [validChartColumns]);

  // 1. Updated Data Processing
  // --- Updated CHART DATA PROCESSING with Filter Logic ---
  const { processedChartData, maxSegments } = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return { processedChartData: [], maxSegments: 0 };
    }

    let globalMaxSegments = 0;

    const processed = filteredData.map((row) => {
      const segments = validChartColumns
        .map((col) => ({
          label: col,
          value: row[col] || 0,
          color: dynamicColorMap[col],
        }))
        .filter((s) => {
          const hasValue = s.value > 0;
          // The fix: This line now respects the chart filter badges
          const isSelected = activeChartFilters.length === 0 || activeChartFilters.includes(s.label);
          return hasValue && isSelected;
        })
        .sort((a, b) => b.value - a.value);

      if (segments.length > globalMaxSegments) globalMaxSegments = segments.length;

      const resultRow = {
        name: row.Executive,
        total: segments.reduce((sum, s) => sum + s.value, 0),
        UID: row.UID
      };

      segments.forEach((seg, index) => {
        resultRow[`stack${index}`] = seg.value;
        resultRow[`stack${index}Color`] = seg.color;
        resultRow[`stack${index}Label`] = seg.label;
      });

      return resultRow;
    })
      .filter(p => p.total > 0)
      .sort((a, b) => b.total - a.total);

    return { processedChartData: processed, maxSegments: globalMaxSegments };
  }, [filteredData, validChartColumns, dynamicColorMap, activeChartFilters]);

  useEffect(() => {
    const handleHeaderFilterClick = () => {
      if (isMobile) {
        setIsDrawerOpen(true);
        setIsFilterSelected(true);
      } else {
        setIsFilterSelected((prev) => !prev);
      }
    };

    window.addEventListener("open-efforts-log-filter", handleHeaderFilterClick);
    return () => window.removeEventListener("open-efforts-log-filter", handleHeaderFilterClick);
  }, [isMobile]);
  
  // const chartHeight = useMemo(() => {
  //   const barHeight = 45;
  //   const headerHeight = 50;
  //   const filterHeight = activeChartFilters.length > 0 || validChartColumns.length > 0 ? 60 : 0;

  //   const calculated = (processedChartData.length * barHeight) + headerHeight + filterHeight;

  //   return processedChartData.length > 0 ? calculated : 0;
  // }, [processedChartData, activeChartFilters, validChartColumns]);

  const chartHeight = useMemo(() => {
    if (processedChartData.length === 0) return 0;

    const headerHeight = 44; // CardHeader height
    const barRowHeight = 45; // Fixed height per executive (bar + spacing)

    // Badge Area Calculation
    const categoriesCount = validChartColumns.length;
    const filterRows = isMobile ? Math.ceil(categoriesCount / 2) : Math.ceil(categoriesCount / 5);
    const badgeAreaHeight = categoriesCount > 0 ? (filterRows * 35) + 15 : 0;

    const bottomBuffer = 20; // Space for X-Axis/Bottom padding

    // Total height of the Card
    return headerHeight + badgeAreaHeight + (processedChartData.length * barRowHeight) + bottomBuffer;
  }, [processedChartData, validChartColumns, isMobile]);
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // We filter out any undefined stacks and sort by value for display
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-100 min-w-[120px]">
          <p className="text-black mb-2 border-b pb-1 font-bold">{label}</p>
          {payload
            .filter(p => p.value !== undefined)
            .map((entry, index) => (
              <div key={index} className="flex items-center gap-2 my-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: entry.payload[`${entry.dataKey}Color`] }}
                />
                <span className="text-xs font-medium">
                  {entry.payload[`${entry.dataKey}Label`]}:
                </span>
                <span className="text-xs ml-auto font-bold">{entry.value}</span>
              </div>
            ))}
        </div>
      );
    }
    return null;
  };
  const toggleChartFilter = (type) => {
    setActiveChartFilters((prev) => {
      if (prev.includes(type)) return prev.filter((t) => t !== type);
      return [...prev, type];
    });
  };

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  // -----------------------------

  const handleResetFilters = () => {
    const monthFilters = getMonthFilterFromNumber(monthParam);
    const cleared = {
      assignedTo: "all",
      dateRange: monthFilters.dateRange,
      otherMonth: monthFilters.otherMonth,
      otherQuarter: "all",
      otherYear: "all"
    };
    setSelectedFilters(cleared);
    setTempFilters(cleared);
    setIsDrawerOpen(false);
    setSortConfig({ key: null, direction: null });
    setActiveChartFilters([]);
  };

  const handleCancel = () => {
    setTempFilters(selectedFilters);
    setIsFilterSelected(false);
    setIsDrawerOpen(false);
  };

  const redirectOnRecords = (row, col) => {
    // Determine the period parameter and additional params based on selected date range
    let periodParam = "This Month"; // Default to "This Month"
    let additionalParams = "";
    switch (selectedFilters.dateRange) {
      case "This Month":
        periodParam = "This Month";
        break;
      case "Other Month":
        periodParam = "Other Month";
        if (selectedFilters.otherMonth && selectedFilters.otherMonth !== "all") {
          additionalParams = `&om=${encodeURIComponent(selectedFilters.otherMonth)}`;
        }
        break;
      case "This Quarter":
        periodParam = "This Quarter";
        break;
      case "Other Quarter":
        periodParam = "Other Quarter";
        if (selectedFilters.otherQuarter && selectedFilters.otherQuarter !== "all") {
          additionalParams = `&oq=${encodeURIComponent(selectedFilters.otherQuarter)}`;
        }
        break;
      case "This Year":
        periodParam = "This Year";
        break;
      case "Other Year":
        periodParam = "Other Year";
        if (selectedFilters.otherYear && selectedFilters.otherYear !== "all") {
          additionalParams = `&oy=${encodeURIComponent(
            selectedFilters.otherYear
          )}`;
        }
        break;
      default:
        periodParam = "This Month";
    }

    // Build the URL with p and eid parameters
    let url = '';
    if (["Executive", "Orders", "Value"].includes(col)) {
      url = `records?p=${encodeURIComponent(periodParam)}&eid=${row.UID
        }${additionalParams}`;
    } else {
      let typeID = recordTypes.find((rt) => rt.Typ === col)?.TID || 0;
      url = `records?p=${encodeURIComponent(periodParam)}&eid=${row.UID
        }${additionalParams}&iid=${typeID}`;
    }

    // Navigate to the URL
    if (!isMobile) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    else {
      navigate("/" + url);
    }

  };
  const currentRecords = useMemo(() => {
    if (isMobile) return sortedData; // Show all on mobile if following Records logic
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, startIndex, endIndex, isMobile]);
  // Desktop Table
  const WebDataTable = () => {
    // 1. Calculate Totals for the Footer
    const totals = useMemo(() => {
      return sortedData.reduce((acc, row) => {
        // Logic for dynamic columns + the new Orders column
        const allCols = [...dynamicColumns, "Orders"];
        allCols
          .filter((col) => columnVisibility[col] !== false)
          .forEach((col) => {
            acc[col] = (acc[col] || 0) + (Number(row[col]) || 0);
          });
        return acc;
      }, {});
    }, [sortedData, dynamicColumns, columnVisibility]);

    // 2. Prepare the full column list (Dynamic + Orders)
    const displayColumns = useMemo(() => {
      const cols = [...dynamicColumns];
      const valueIndex = cols.indexOf("Value");
      if (valueIndex !== -1) {
        cols.splice(valueIndex + 1, 0, "Orders");
      } else {
        cols.push("Orders");
      }
      return cols.filter((col) => columnVisibility[col] !== false);
    }, [dynamicColumns, columnVisibility]);

    return (
      <>
        <Card className="bg-sahi-white hidden lg:block !pt-0 border border-gray-200 shadow-sm sahi-radius overflow-hidden">
          <CardContent className="px-0 !sahi-radius">
            <div className="overflow-x-auto">
              <Table className="min-w-full table-auto">
                <TableHeader className="bg-gray-50/50">
                  {/* FIX 2: Change bg-sahi-white to transparent or gray-50/50 */}
                  <TableRow className="bg-transparent border-b-2 border-gray-200">
                    {/* Executive Header - Left Aligned */}
                    <TableHead
                      className="h-11 font-semibold text-sm text-black whitespace-nowrap px-4 align-middle text-left"
                      onClick={() => sortData("UID")}
                    >
                      <div className="flex items-center gap-1 justify-start">Executive</div>
                    </TableHead>

                    {/* All Other Headers - Right Aligned */}
                    {displayColumns.map((col) => {
                      const isAmount = col === "Value";
                      return (
                        <TableHead
                          key={col}
                          className="h-11 px-4 text-sm font-semibold text-black align-middle cursor-pointer text-right"
                          onClick={() => sortData(col)}
                        >
                          <div className="flex items-center gap-1 justify-end">
                            {isAmount ? (
                              <label className="text-sm font-semibold text-black whitespace-nowrap cursor-pointer">
                                Value ({currencySymbol})
                              </label>
                            ) : (
                              col
                            )}
                            {sortConfig.key === col && (
                              sortConfig.direction === "asc" ? (
                                <ArrowUp className="h-4 w-4 ml-1" />
                              ) : (
                                <ArrowDown className="h-4 w-4 ml-1" />
                              )
                            )}
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {sortedData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="hover:bg-gray-50 border-b border-gray-200 transition-colors"
                    >
                      {/* Executive Data - Left Aligned */}
                      <TableCell
                        className="px-4 text-left font-medium py-2.5 pl-4 align-middle cursor-pointer max-w-[200px]" // Added max-width
                        onClick={() => redirectOnRecords(row, "Executive")}
                      >
                        <div
                          className="truncate"
                          title={row.Executive} // Shows full name on hover
                        >
                          {row.Executive || "-"}
                        </div>
                      </TableCell>

                      {/* All Other Data - Right Aligned */}
                      {displayColumns.map((col) => {
                        const isAmount = col === "Value";
                        return (
                          <TableCell
                            key={col}
                            className="px-4 py-2.5 cursor-pointer align-middle text-right"
                          >
                            {isAmount ? (
                              <FormatAmount
                                amount={row[col] || 0}
                                currency={currencySymbol}
                                showCurr={false}
                              />
                            ) : (
                              (row[col] || 0).toLocaleString()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>

                {/* Footer - Styled to match Header exactly */}
                <tfoot className="bg-gray-50/50 border-t border-gray-200">
                  <TableRow className="h-11 hover:bg-transparent">
                    <TableCell className="text-left pl-4 font-bold text-sm text-black align-middle">
                      Total
                    </TableCell>

                    {displayColumns.map((col) => {
                      const isAmount = col === "Value";
                      return (
                        <TableCell
                          key={`total-${col}`}
                          className="h-11 px-4 font-bold text-sm text-black align-middle text-right"
                        >
                          {isAmount
                            ? 
							<FormatAmount
                                amount={totals[col] || 0}
                                currency={currencySymbol}
                                showCurr={false}
                              />
                            : (totals[col] || 0).toLocaleString()}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </tfoot>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(size) => setRowsPerPage(size)}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </>
    );
  };
  // Mobile Card
  const MobileCard = ({ row }) => {
    const user = users.find((u) => u.userID === row.UID);
    const [isExpanded, setIsExpanded] = useState(false);

    const totalInteractions = useMemo(() => {
      return dynamicColumns.reduce((sum, col) => {
        // Exclude Value
        if (col === "Value") return sum;
        if (col === "Sales" || col === "Sale") return sum;
        if (columnVisibility[col] === false) return sum;

        return sum + (row[col] || 0);
      }, 0);
    }, [row, dynamicColumns, columnVisibility]);

    const orderCount = row.Orders || 0;
    const orderValue = row.Value || 0;

    return (
      <div className="bg-sahi-white sahi-radius shadow-sm border border-gray-200 mb-2 w-full f-col justify-between">
        {/* === Header Section === */}
        <div
          className="f-col gap-1 mobile-tile-header-layout cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          <div className="f-row justify-between gap-2">
            {/* User Name */}
            <div className="f-col min-w-0">
              <p className="font-semibold text-text-color text-sm truncate max-w-full">
                {user?.name || "-"}
              </p>
            </div>

            <div className="flex justiby-between f-row items-center gap-1">
              <span className={`h-4 w-4 transition-transform duration-200 mr-1 mb-1`}> {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}</span>
              <span className="text-sm font-semibold text-green-700">
                {currencySymbol} {formatAmount(orderValue, countryID).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* === Footer Section === */}
        <div className="f-col w-full">
          <div
            className="flex justify-between items-center text-xs border-t border-[#f1f1f1] w-full mobile-tile-footer-layout px-2"
          >
            <div className="flex items-center">
              <span className="text-xs text-sub-text-color">
                <strong className="text-text-color">{totalInteractions}</strong>{" "}
                {totalInteractions <= 1 ? "Interaction" : "Interactions"}
              </span>
            </div>

            <div className="flex items-center">
              <span className="text-xs text-sub-text-color">
                <strong className="text-text-color">{orderCount}</strong>{" "}
                {orderCount <= 1 ? "Sale" : "Sales"}
              </span>
            </div>
          </div>

          {/* === Expanded Details Section === */}
          {isExpanded && (
            <div className="p-2 border-t border-[#f1f1f1]">
              <div className="flex flex-wrap gap-2">
                {dynamicColumns
                  .filter((col) => !["value", "orders"].includes(col.toLocaleLowerCase()))
                  .filter(col => columnVisibility[col] !== false)
                  .map((col) => {
                    const value = row[col] || 0;

                    // Logic: Only show if there is a count to keep mobile view clean
                    if (value === 0) return null;

                    return (
                      <div
                        key={col}
                        className="inline-flex items-center gap-2 bg-gray-100 px-3 py-2 rounded text-sm active:bg-gray-200"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent closing the card
                          redirectOnRecords(row, col);
                        }}
                      >
                        <div className="inline-flex items-center gap-1 min-w-0">
                          <span className="text-sub-text-color whitespace-nowrap">
                            {col}:
                          </span>
                          {/* FIX: Render the actual value here */}
                          <span className="font-bold text-text-color">
                            {value.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const isDefaultFilters = useMemo(() => {
    return (
      selectedFilters.assignedTo === "all" &&
      selectedFilters.dateRange === "This Month" &&
      selectedFilters.otherMonth === "all" &&
      selectedFilters.otherQuarter === "all" &&
      selectedFilters.otherYear === "all"
    );
  }, [selectedFilters]);
  const hasActiveFilters = searchTerm.trim() !== "" || !isDefaultFilters;

  const filterButton = useMemo(() => {
    let activeFilterClass =
      isFilterSelected && !isMobile ? "filter-active" : "";
    let filterIconToggleClass =
      "h-4 w-4 " +
      (isMobile
        ? ""
        : isFilterSelected
          ? "text-sahi-primary-color"
          : "text-white group-hover:text-sahi-primary-color");
    return (
      <Button
        className={cn(
          "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px]",
          activeFilterClass
        )}
        onClick={() => {
          if (isMobile) {
            setIsDrawerOpen(true);
            setIsFilterSelected(true);
          } else {
            setIsFilterSelected(!isFilterSelected);
          }
        }}
        aria-label="Toggle Filter"
      >
        <div className="h-6 w-6 flex items-center justify-center transition-all">
          <Filter className={cn(filterIconToggleClass)} />
        </div>
      </Button>
    );
  }, [isMobile, isDefaultFilters]);

  return (
    <div className="space-y-3 lg:space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="hidden lg:flex justify-between items-center gap-3">
        <div className="flex items-center flex-grow">
          <h1 className="text-3xl font-bold text-text-color mr-4 web-header-name">
            Team Efforts Summary
          </h1>
          {/* Search Box (Show if > 10 records or searching) */}
          {(effortsData.length > 10 || searchTerm) && (
            <div className="flex items-center justify-end flex-grow">
              <div className="relative w-120 filter-border">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                <Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none"
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {effortsData.length > 0 && (
            <>
              <ColumnPreferencesDropdown
                type="EFFORTLOG"
                columns={columnDefinitions}
                columnVisibility={columnVisibility}
                onUpdate={(newVisibility) => setColumnVisibility(newVisibility)}
              />
            </>
          )}
        </div>
        {/* <div className="flex items-center gap-3 h-full">
           {effortsData.length > 0 && (
            <Button
              className={cn(
                "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px] relative",
                isFilterSelected && "filter-active"
              )}
              onClick={() => setIsFilterSelected(prev => !prev)}
              aria-label="Toggle filter"
            >
              <div className="h-6 w-6 flex items-center justify-center transition-all">
                <Filter
                  className={cn(
                    "h-4 w-4",
                    isFilterSelected
                      ? "text-sahi-primary-color"
                      : "text-white group-hover:text-sahi-primary-color"
                  )}
                />
              </div>
            </Button>
          )} 
        </div> */}
      </div>

      {/* Mobile view */}
      <ConditionalDiv className="f-col lg:hidden gap-3">
        {/* Search and Filter (Show if > 10 records or searching) */}
        {(effortsData.length > 10 || searchTerm) && (
          <div className="w-full flex items-center gap-2 pt-0">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white " />
              <Input
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 w-full"
              />
            </div>
            {/* {filterButton} */}
          </div>
        )}
      </ConditionalDiv>

      {/* Filters */}
      {isFilterSelected &&
        (isMobile ? (
          <Drawer
            open={isDrawerOpen}
            onOpenChange={() => {
              setIsDrawerOpen(false);
              setIsFilterSelected(false);
            }}
          >
            <DrawerContent className="p-0 [&>div:first-child]:hidden">
              <div className="f-col h-[100dvh] !rounded-t-[10px]">
                {/* Header */}
                <div className="flex plain items-center justify-between p-4 border-b-2 border-gray-200 text-xl !rounded-t-[10px]">
                  <p className="text-lg font-semibold text-sahi-primary-color">
                    Filters
                  </p>
                  <button
                    className="group flex items-center gap-1 text-sm font-light hover:underline transition duration-200 text-sahi-primary-color"
                    onClick={() => {
                      handleResetFilters();
                      setIsFilterSelected(false);
                    }}
                  >
                    <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
                    <span>Reset</span>
                  </button>
                </div>
                {/* Main Content */}
                <div className="flex flex-1 overflow-y-auto">
                  {/* Scrollable Filters */}
                  <div className="flex-1 p-3 overflow-y-auto filter-scroll-container">
                    {filterOptions.map(({ key, label, options, displayValue, type, mobiledisabled, mobileDropdownText, mobileLabel, showAllOption, mobileNoLabel }) => {
                      // Only show sub-filters when their parent dateRange is selected
                      const shouldShow =
                        key === "dateRange" ||
                        (key === "otherMonth" && tempFilters.dateRange === "Other Month") ||
                        (key === "otherQuarter" && tempFilters.dateRange === "Other Quarter") ||
                        (key === "otherYear" && tempFilters.dateRange === "Other Year");

                      if (!shouldShow) return null;

                      return (
                        <div key={key} id={key} className="mb-4">
                          <DrawerFilter
                            label={label}
                            options={options}
                            value={tempFilters[key]}
                            displayValue={displayValue}
                            onChange={(value) => {
                              setTempFilters((prev) => ({
                                ...prev,
                                [key]: value,
                              }));
                            }}
                            disabled={mobiledisabled}
                            type={type}
                            mobileDropdownText={mobileDropdownText}
                            MobileLabel={mobileLabel}
                            showAllOption={showAllOption}
                            mobileNoLabel={mobileNoLabel}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Always Visible Footer */}
                <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-sahi-white">
                  <DrawerClose>
                    <div className="f-row justify-start gap-2">
                      <Button
                        className="sahi-primary-button"
                        onClick={() => {
                          setSelectedFilters(tempFilters);
                          setIsDrawerOpen(false);
                          setIsFilterSelected(false);
                        }}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        className="sahi-secondary-button"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        ) : (
          <div className="hidden lg:flex flex-wrap gap-4 mt-4">
            <PeriodFilter
              value={selectedFilters}
              onChange={(newFilters) => {
                setSelectedFilters(newFilters);
                setTempFilters(newFilters);
              }}
              oldestDate={oldestSinceDT}
              showMonthFilter={true}
              showQuarterFilter={true}
              showYearFilter={true}
            />
          </div>
        ))}

      {/* --- DATA SECTION (Grid Layout) --- */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 w-full">
          <Loader2 className="h-8 w-8 animate-spin text-sahi-primary-color mr-3" />
          <span>Loading data...</span>
        </div>
      ) : effortsData.length === 0 ? (
        <div className="flex flex-col items-start px-4 lg:px-0 pt-5">
          <div className="f-col items-start gap-5 mt-4 pr-4">
            <div className="no-records border border-gray-200 shadow-sm sahi-radius p-4 bg-white flex items-center w-full md:w-fit">Analyze team interactions by day or period for performance insights.
              <p className="text-sm font-semibold">No data available right now</p>
            </div>
          </div>
        </div>
      ) : filteredData.length === 0 ? (
        /* CASE 2: SEARCH/FILTER EMPTY (Your requested UI) */
        <div className="f-col items-start gap-3 mt-0">
          <p className="no-records border border-gray-200 shadow-sm sahi-radius">
            <span>
              No efforts found{hasActiveFilters ? ' for selected filters.' : '.'}
            </span>
            {hasActiveFilters && (
              <span
                onClick={() => {
                  setSearchTerm("");
                  handleResetFilters();
                }}
                className="text-sahi-primary-color underline hover:text-sahi-primary-color/80 cursor-pointer ml-2"
              >
                Clear filter.
              </span>
            )}
          </p>
        </div>
      ) : (
        <div
          className={cn(
            "grid grid-cols-1 gap-2 w-full items-start",
            processedChartData.length > 0 ? "lg:grid-cols-[1fr_700px]" : "lg:grid-cols-1"
          )}
        >
          {processedChartData.length > 0 && (
            <div className="w-full order-1 lg:order-2 mb-4 lg:mb-0">
              <Card
                className="bg-sahi-white shadow-sm border border-gray-200 sticky top-4 sahi-radius overflow-hidden flex flex-col"
              >
                <CardHeader className="h-11 py-0 px-4 flex flex-row items-center justify-center border-b border-gray-100 bg-white">
                  {" "}
                  <CardTitle className="text-sm font-semibold ">
                    Executive vs Overall Efforts
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 flex flex-col h-full">
                  <div
                    className="flex flex-wrap gap-2 mb-2 px-4 mt-3"
                    style={{ marginLeft: isMobile ? "8px" : "8px" }}
                  >
                    {validChartColumns.map((type) => {
                      const isActive = activeChartFilters.length === 0 || activeChartFilters.includes(type);
                      const color = dynamicColorMap[type];
                      return (
                        <Badge
                          key={type}
                          onClick={() => toggleChartFilter(type)}
                          className={cn(
                            "cursor-pointer px-3 py-1.5 text-[11px] font-semibold transition-all select-none border-2 rounded-md shadow-sm hover:scale-105 active:scale-95",
                            isActive
                              ? "text-white border-transparent ring-1 ring-offset-1"
                              : "bg-gray-100 text-gray-600 border-gray-200 hover:border-sahi-primary-color/50 hover:bg-white"
                          )}
                          style={{
                            backgroundColor: isActive ? color : undefined,
                            borderColor: isActive ? color : undefined,
                            boxShadow: isActive
                              ? `0 4px 6px ${color}30`
                              : "none",
                          }}
                        >
                          <span className="flex items-center gap-1.5">
                            {/* Always show the color dot so it looks like a filter key */}
                            <div
                              className={cn(
                                "w-2 h-2 rounded-full",
                                isActive ? "bg-white" : ""
                              )}
                              style={{
                                backgroundColor: isActive ? undefined : color,
                              }}
                            />
                            {type}
                          </span>
                        </Badge>
                      );
                    })}
                    {activeChartFilters.length > 0 && (
                      <Badge
                        onClick={() => setActiveChartFilters([])}
                        variant="outline"
                        className="cursor-pointer px-3 py-1 text-[11px] text-gray-400 hover:text-red-500 border-dashed rounded-full"
                      >
                        Clear
                      </Badge>
                    )}
                  </div>
                </CardContent>
                <CardContent className="p-0">
                  <div
                    className="w-full p-2 text-xs"
                    style={{
                      height: isMobile
                        ? Math.max(300, processedChartData.length * 50)
                        : chartHeight || 400,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        layout="vertical"
                        data={processedChartData}
                        margin={{ top: 5, right: 40, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                        <XAxis type="number" hide />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={120} // Slightly increased to allow more room before truncation
                          tickLine={false}
                          axisLine={false}
                          tick={(props) => {
                            const { x, y, payload } = props;
                            const fullText = payload.value;
                            // Truncation logic: if longer than 15 chars, truncate
                            const truncatedText = fullText.length > 15
                              ? `${fullText.substring(0, 12)}...`
                              : fullText;

                            return (
                              <g transform={`translate(${x},${y})`}>
                                <text
                                  x={-8}
                                  y={0}
                                  dy={4}
                                  textAnchor="end"
                                  fill="#6b7280"
                                  style={{
                                    fontSize: 11,
                                    outline: 'none',
                                    cursor: 'default',
                                    userSelect: 'none',
                                  }}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  <title>{fullText}</title>
                                  {truncatedText}
                                </text>
                              </g>
                            );
                          }}
                        />

                        <RechartsTooltip
                          content={<CustomTooltip />}
                          cursor={{ fill: 'transparent' }}
                          allowEscapeViewBox={{ x: false, y: false }}
                          // position={{ y: 0 }}
                          coordinate={{ x: 0, y: 0 }}
                          contentStyle={{
                            borderRadius: "8px",
                            border: "none",
                            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                          }}
                        />

                        {[...Array(maxSegments)].map((_, i) => (
                          <Bar
                            key={`stack${i}`}
                            dataKey={`stack${i}`}
                            stackId="a"
                            barSize={22}
                            animationDuration={500}
                            activeBar={false}
                            style={{ outline: 'none' }}
                          >
                            {processedChartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry[`stack${i}Color`]}
                                tabIndex={-1}
                                style={{ outline: 'none', border: 'none', pointerEvents: 'auto' }}
                                onMouseDown={(e) => e.preventDefault()}
                                onFocus={(e) => e.target.blur()}
                              />
                            ))}
                            <LabelList
                              dataKey={`stack${i}`}
                              content={(props) => {
                                const { x, y, width, height, index } = props;
                                const row = processedChartData[index];
                                if (!row) return null;
                                const isLast = row[`stack${i + 1}`] === undefined;
                                if (!isLast) return null;
                                return (
                                  <text x={x + width + 5} y={y + height / 2} fill="#374151" fontSize="10" fontWeight="bold" dominantBaseline="middle">
                                    {row.total}
                                  </text>
                                );
                              }}
                            />
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>              </Card>
            </div>
          )}

          {/* LEFT COLUMN (Table) */}
          <div
            className={cn(
              "w-full min-w-0",
              processedChartData.length > 0 ? "order-2 lg:order-1" : "order-1"
            )}
          >
            {isMobile ? (
              <div className="block lg:hidden">
                <div className="space-y-2">
                  {sortedData.map((row, index) => (
                    <MobileCard key={index} row={row} />
                  ))}
                </div>
              </div>
            ) : (
              <WebDataTable />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EffortsLog;